import React, { useRef, useEffect, useId } from "react";
import { Controller } from "react-hook-form";
import ClearIcon from "@mui/icons-material/Clear";
import {
  ErrorMessage,
  IconWrapper,
  InputWrapper,
  InputFieldWrapper,
  StyledInput,
} from "./styles";
import { IconButton } from "../../IconButton";
import { FormInputProps, InputFieldProps } from "./types";

export const FormInput = ({
  name,
  control,
  rules,
  type = "text",
  handleClear = false,
  disabled = false,
  multiline = false,
  placeholder = "",
  autoFocus = false,
  autoComplete,
  inputMode,
  variant = "default",
  id,
  required = false,
}: FormInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [autoFocus]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => (
        <InputField
          inputRef={inputRef}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          error={error}
          type={type}
          disabled={disabled}
          multiline={multiline}
          placeholder={placeholder}
          handleClear={handleClear}
          autoComplete={autoComplete}
          inputMode={inputMode}
          variant={variant}
          id={id}
          required={required}
        />
      )}
    />
  );
};

const InputField = ({
  inputRef,
  value,
  onChange,
  onBlur,
  error,
  type,
  disabled,
  multiline,
  placeholder,
  handleClear,
  autoComplete,
  inputMode,
  variant,
  id,
  required,
}: InputFieldProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  return (
    <InputWrapper>
      <InputFieldWrapper>
        <StyledInput
          ref={inputRef}
          id={inputId}
          as={multiline ? "textarea" : "input"}
          type={type}
          $error={!!error}
          $variant={variant}
          $hasClear={Boolean(
            !disabled && handleClear && value && value.toString().length > 0,
          )}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          value={value ?? ""}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          onBlur={onBlur}
          onChange={
            type === "number"
              ? (e: any) => {
                  const val = e.target.value;
                  onChange(val === "" ? "" : Number.parseInt(val, 10));
                }
              : onChange
          }
          onWheel={
            type === "number"
              ? (e: React.WheelEvent<HTMLInputElement>) =>
                  e.currentTarget.blur()
              : undefined
          }
        />
        {!disabled && handleClear && value && value.toString().length > 0 && (
          <IconWrapper>
            <IconButton
              size="small"
              onClick={() => {
                type === "number" ? onChange(0) : onChange("");
              }}
              aria-label="Clear value"
            >
              <ClearIcon />
            </IconButton>
          </IconWrapper>
        )}
      </InputFieldWrapper>
      {error && (
        <ErrorMessage id={errorId} role="alert">
          {error.message}
        </ErrorMessage>
      )}
    </InputWrapper>
  );
};
